import {Icon} from '@/app/components/Icon';
import {useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {InfoSection} from '../../../app/components/InfoSection';
import {useGetBiobookUpdatesQuery} from '../../../app/services/biobookApi';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useGetBiotwinQuery} from '../../../app/services/biotwinApi';
import {getErrorMessage} from '../../../app/utils/miscUtils';
import {selectSession} from '../../auth/authSlice';
import {useReadOnly} from '../../biotwin/hooks/useReadOnly';
import {useAssistantUtils} from '../../core/hooks/useAssistantUtils';
import {useDemo} from '../../demo/hooks/useDemo';
import {BiobookPage} from '../components/BiobookPage';
import {BiobookPageLocked} from '../components/BiobookPageLocked';
import {BiobookPageSkeleton} from '../components/BiobookPageSkeleton';
export const Updates = () => {
  const params = useParams<{
    userId: string;
  }>();
  const session = useAppSelector(selectSession);
  const userId = Number(params.userId ?? session?.id ?? 0);
  const {
    data: biotwin,
    error: errorGetBiotwin,
    isFetching: isFetchingGetBiotwin,
  } = useGetBiotwinQuery({
    id: userId,
  });
  const {isReadOnly} = useReadOnly();
  const readOnly = isReadOnly(biotwin);
  const {
    data: updates,
    error,
    isFetching,
  } = useGetBiobookUpdatesQuery(
    {
      userId,
    },
    {
      skip: !biotwin || readOnly,
    },
  );
  const {isDemo, showLimitReachedDialog} = useDemo();
  const {pushAssistantContent} = useAssistantUtils();
  useEffect(() => {
    if (isDemo) {
      return pushAssistantContent({
        detail: 'As new guidelines are added, you can see the updates of the biobook here.',
        detailDelay: 1000,
        summary: 'Here are the updates.',
      });
    }
  }, [isDemo]);
  return (
    <div className="flex-1 flex flex-col gap-6 min-h-full p-6 overflow-auto">
      {isFetchingGetBiotwin && <BiobookPageSkeleton />}
      {!isFetchingGetBiotwin && errorGetBiotwin && (
        <InfoSection
          className="flex-1"
          icon="exclamation-circle"
          message={getErrorMessage(errorGetBiotwin)}
          title="Error"
        />
      )}
      {!isFetchingGetBiotwin && readOnly && (
        <InfoSection className="flex-1" icon="info-circle" message="There are no new pages, yet." title="No Updates" />
      )}
      {isFetching && <BiobookPageSkeleton />}
      {isDemo && !isFetching && (error || !updates?.length) && (
        <InfoSection
          className={'flex-1'}
          icon={'lock'}
          title={'Always Stay Updated!'}
          message={'Please sign up to unlock the newest information.'}
          actions={[
            {
              type: 'button',
              props: {
                startIcon: <Icon icon={'lock-open'} />,
                children: 'Unlock',
                onClick: () => {
                  showLimitReachedDialog({
                    title: 'Always Stay Updated!',
                  });
                },
                variant: 'default',
              },
            },
          ]}
        />
      )}
      {isDemo && !isFetching && updates != null && updates.length > 0 && (
        <>
          {updates.map((update) => (
            <BiobookPage biobookPage={update} key={update.phraseId} showBodyLifeMapLabels userId={userId} />
          ))}
          {!!biotwin?.biobookUpdatePhraseIds?.length &&
            Array.from({
              length: biotwin.biobookUpdatePhraseIds.length - updates.length,
            }).map((_, index) => <BiobookPageLocked key={index} userId={userId} />)}
        </>
      )}
      {!isDemo && !isFetching && error && (
        <InfoSection className="flex-1" icon="exclamation-circle" message={getErrorMessage(error)} title="Error" />
      )}
      {!isDemo && !isFetching && !error && updates != null && updates.length === 0 && (
        <InfoSection className="flex-1" icon="info-circle" message="There are no new pages, yet." title="No Updates" />
      )}
      {!isDemo &&
        !isFetching &&
        !error &&
        updates != null &&
        updates.length > 0 &&
        updates.map((update) => (
          <BiobookPage
            biobookPage={update}
            key={update.phraseId}
            showBodyLifeMapLabels
            showMatchedConditions
            userId={userId}
          />
        ))}
    </div>
  );
};
